import { useEffect, useState } from 'react';
import { PageHeader } from '@/components/layout/PageHeader';
import { financeApi } from '@/api/finance.api';
import type { FinanceAnalytics } from '@/types/finance';
import { showApiError } from '@/utils/apiError';

function SliceTable({ title, slices }: { title: string; slices: Array<{ label: string; amount: number }> }) {
  return (
    <div className="card p-4">
      <h3 className="font-semibold mb-3">{title}</h3>
      {slices.length === 0 ? (
        <p className="text-sm text-slate-500">No data yet.</p>
      ) : (
        <ul className="space-y-2 text-sm">
          {slices.map((s) => (
            <li key={s.label} className="flex justify-between gap-4 border-b pb-2">
              <span>{s.label}</span>
              <span className="tabular-nums font-medium">{s.amount.toFixed(2)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function FinanceAnalyticsPage() {
  const [analytics, setAnalytics] = useState<FinanceAnalytics | null>(null);

  useEffect(() => {
    financeApi.platformAnalytics().then(setAnalytics).catch((e) => showApiError(e, 'Failed to load analytics'));
  }, []);

  return (
    <div>
      <PageHeader
        title="Enterprise analytics"
        description="Revenue, expenses, and aging analytics — drill-down without accounting complexity"
        breadcrumbs={[
          { label: 'Org', to: '/org' },
          { label: 'Finance', to: '/finance' },
          { label: 'Reports', to: '/finance/reports' },
          { label: 'Analytics' },
        ]}
      />

      {analytics && (
        <div className="grid gap-4 md:grid-cols-2">
          <SliceTable title="Revenue accounts" slices={analytics.revenueByAccountType} />
          <SliceTable title="Expense accounts" slices={analytics.expenseByAccountType} />
          <SliceTable title="AR aging" slices={analytics.arAgingBuckets} />
          <SliceTable title="AP aging" slices={analytics.apAgingBuckets} />
        </div>
      )}
    </div>
  );
}
